import { Button, Group, Modal, NumberInput, SimpleGrid, Stack, Switch, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import { useDocTypes } from "../dms/DmsPage";
import AdminLayout from "./AdminLayout";
import { ScannersPanel } from "./ScannersPanel";

interface DT { code: string; name: string; classification?: string; category_no?: string; retention_years?: number | null; archival?: boolean; legal_basis?: string; active?: boolean }

export default function DocTypesPage() {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const types = useDocTypes();
  const nav = useNavigate();
  const [edit, setEdit] = useState<DT | null>(null);
  const save = useMutation({ mutationFn: (d: DT) => http.put(`/api/v1/dms/doc-types/${d.code}`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-types"] }); setEdit(null); }, onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  return (
    <AdminLayout>
      <Stack>
        <PageHeader title="DMS status i tipovi dokumenata" description="Lista kategorija registraturske građe: klasifikaciona oznaka, rok čuvanja i da li je arhivska građa (Zakon o arhivskoj djelatnosti RS 119/08; Uredba o kancelarijskom poslovanju). Rokove usaglasiti sa Arhivom Republike Srpske."
          right={<>
            <Button variant="default" onClick={() => nav("/dms")}>{t("Dokumenti, arhivska knjiga, odabiranje")}</Button>
            {can("dms:admin") && <Button leftSection={<IconPlus size={14} />} onClick={() => setEdit({ code: "", name: "", retention_years: 5, archival: false, active: true })}>{t("Novi tip")}</Button>}
          </>} />
        <DataTable rows={(types.data ?? []) as unknown as Record<string, unknown>[]} exportName="tipovi_dokumenata" onRowClick={(r) => can("dms:admin") && setEdit(r as unknown as DT)} columns={[
          { key: "code", label: "Kod" }, { key: "name", label: "Naziv" }, { key: "classification", label: "Klas. oznaka" }, { key: "category_no", label: "Kategorija" },
          { key: "retention_years", label: "Rok čuvanja (god.)", type: "integer" }, { key: "archival", label: "Arhivska građa", type: "bool" },
          { key: "legal_basis", label: "Pravni osnov" },
        ]} />
        {can("dms:admin") && <ScannersPanel />}
        {edit && (
          <Modal opened onClose={() => setEdit(null)} title={edit.code || t("Novi tip dokumenta")} size="lg">
            <Stack>
              <SimpleGrid cols={2}>
                <TextInput label={t("Kod")} disabled={!!types.data?.some((x) => x.code === edit.code)} value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.currentTarget.value })} />
                <TextInput label={t("Naziv")} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.currentTarget.value })} />
                <TextInput label={t("Klasifikaciona oznaka")} value={edit.classification ?? ""} onChange={(e) => setEdit({ ...edit, classification: e.currentTarget.value })} />
                <TextInput label={t("Redni broj u listi kategorija")} value={edit.category_no ?? ""} onChange={(e) => setEdit({ ...edit, category_no: e.currentTarget.value })} />
                <NumberInput label={t("Rok čuvanja (godina, prazno = trajno)")} value={edit.retention_years ?? ""} onChange={(v) => setEdit({ ...edit, retention_years: v === "" ? null : Number(v) })} />
                <Switch mt="lg" label={t("Arhivska građa (trajno, predaje se Arhivu RS)")} checked={!!edit.archival} onChange={(e) => setEdit({ ...edit, archival: e.currentTarget.checked })} />
              </SimpleGrid>
              <TextInput label={t("Pravni osnov")} value={edit.legal_basis ?? ""} onChange={(e) => setEdit({ ...edit, legal_basis: e.currentTarget.value })} />
              <Group justify="flex-end"><Button onClick={() => save.mutate(edit)} loading={save.isPending}>{t("Sačuvaj")}</Button></Group>
            </Stack>
          </Modal>
        )}
      </Stack>
    </AdminLayout>
  );
}
